import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';

/** O que recebe foco pelo Tab dentro do modal. */
const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** O mesmo intervalo dos motivos das rotas de atendimentos na API. */
export const CONFIRM_REASON_MIN = 3;
export const CONFIRM_REASON_MAX = 500;

interface ReinforcedConfirmDialogProps {
  title: string;
  /** As consequências da ação, lidas antes de confirmar. */
  description: ReactNode;
  /** A frase da caixa de seleção que o operador marca para dizer que entendeu o efeito. */
  acknowledgement: ReactNode;
  /** Quando dado, o operador digita este texto (o código do pacote) para liberar a confirmação. */
  confirmPhrase?: string;
  /** Quando dado, o motivo é obrigatório (3 a 500 caracteres) e vai para `onConfirm`. */
  reasonLabel?: string;
  confirmLabel: string;
  cancelLabel?: string;
  busy?: boolean;
  /** A recusa da última tentativa; o modal continua aberto. */
  error?: string | null;
  onConfirm: (reason: string | null) => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Confirmação reforçada das ações de atendimentos que não se desfazem (change billing-ai-units,
 * requisitos de `platform-backoffice` e `platform-commercial-actions`): arquivar um pacote,
 * estornar uma cortesia e reembolsar um pacote. O modal mostra as consequências, pede a marca de
 * que o operador entendeu, o motivo quando a rota o grava e, no arquivamento, o código digitado.
 * Mesmo padrão visual e de foco do `StepUpDialog`. Arquivar e estornar não pedem o código do
 * autenticador; o reembolso, que devolve dinheiro, pede (step-up), e a tela abre o `StepUpDialog`
 * depois desta confirmação, com o motivo já confirmado.
 */
export function ReinforcedConfirmDialog({
  title,
  description,
  acknowledgement,
  confirmPhrase,
  reasonLabel,
  confirmLabel,
  cancelLabel = 'Voltar',
  busy = false,
  error = null,
  onConfirm,
  onCancel,
}: ReinforcedConfirmDialogProps) {
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  const dialogRef = useRef<HTMLDivElement>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const phraseRef = useRef<HTMLInputElement>(null);
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const trimmedReason = reason.trim();
  const reasonValid =
    !reasonLabel || (trimmedReason.length >= CONFIRM_REASON_MIN && trimmedReason.length <= CONFIRM_REASON_MAX);
  const phraseValid = !confirmPhrase || typed.trim() === confirmPhrase;
  const canConfirm = acknowledged && reasonValid && phraseValid && !busy;

  useEffect(() => {
    (reasonRef.current ?? phraseRef.current ?? dialogRef.current)?.focus();
  }, []);

  // O foco não sai do modal: o Tab e o Shift+Tab dão a volta dentro dele, e o foco que cai fora
  // volta para o primeiro controle habilitado. Sem isso, o operador mexeria na seção de trás.
  useEffect(() => {
    function focusables(): HTMLElement[] {
      const dialog = dialogRef.current;
      if (!dialog) return [];
      return Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => !(el as HTMLButtonElement).disabled,
      );
    }

    function onKeyDown(e: KeyboardEvent) {
      const dialog = dialogRef.current;
      if (e.key !== 'Tab' || !dialog) return;
      const items = focusables();
      const active = document.activeElement;
      const inside = active instanceof Node && dialog.contains(active);
      if (items.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (!inside || active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || active === last)) {
        e.preventDefault();
        first.focus();
      }
    }

    function onFocusIn(e: FocusEvent) {
      const dialog = dialogRef.current;
      if (!dialog || !(e.target instanceof Node) || dialog.contains(e.target)) return;
      (focusables()[0] ?? dialog).focus();
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, []);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!canConfirm) return;
    void onConfirm(reasonLabel ? trimmedReason : null);
  }

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 40,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !busy) onCancel();
        }}
        style={{
          background: 'var(--color-surface)',
          borderRadius: 12,
          padding: 24,
          maxWidth: 480,
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <h2 id={titleId} style={{ marginTop: 0, fontSize: 18 }}>
          {title}
        </h2>
        <div id={descriptionId} style={{ color: 'var(--color-text-secondary)', marginBottom: 12 }}>
          {description}
        </div>

        <form onSubmit={submit}>
          <fieldset disabled={busy} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            {reasonLabel && (
              <div className="field">
                <label htmlFor={`${id}-reason`}>{reasonLabel}</label>
                <textarea
                  ref={reasonRef}
                  id={`${id}-reason`}
                  rows={2}
                  maxLength={CONFIRM_REASON_MAX}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <div className="muted" style={{ fontSize: 12 }}>
                  De {CONFIRM_REASON_MIN} a {CONFIRM_REASON_MAX} caracteres. Fica na trilha de auditoria.
                </div>
              </div>
            )}

            {confirmPhrase && (
              <div className="field">
                <label htmlFor={`${id}-phrase`}>
                  Digite <span className="mono">{confirmPhrase}</span> para confirmar
                </label>
                <input
                  ref={phraseRef}
                  id={`${id}-phrase`}
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
            )}

            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
              <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
              <span>{acknowledgement}</span>
            </label>

            {error && (
              <div className="error-banner" role="alert" style={{ marginBottom: 8 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" className="btn btn-secondary" onClick={onCancel}>
                {cancelLabel}
              </button>
              <button type="submit" className="btn btn-danger" disabled={!canConfirm}>
                {busy ? `${confirmLabel}…` : confirmLabel}
              </button>
            </div>
          </fieldset>
        </form>
      </div>
    </div>
  );
}
